import { useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '../components/AppShell';
import { Button, Stepper, type StepperItem, StickyFooter } from '../components/ui';
import { OnboardingChecklist } from '../components/wizard/OnboardingChecklist';
import { Step1Identity } from '../components/wizard/Step1Identity';
import { Step2TelegramChannel } from '../components/wizard/Step2TelegramChannel';
import { Step3WhatsAppChannel } from '../components/wizard/Step3WhatsAppChannel';
import { Step4ErpSelector } from '../components/wizard/Step4ErpSelector';
import { Step5BotMode } from '../components/wizard/Step5BotMode';
import { Step6Summary } from '../components/wizard/Step6Summary';
import { wizardNavItems } from '../components/wizard/steps-status';
import {
  type WizardAction,
  type WizardDraft,
  type WizardStep,
  wizardReducer,
} from '../components/wizard/types';

const STORAGE_KEY = 'wizard-draft';

function loadFromLocalStorage(): WizardDraft | null {
  const data = localStorage.getItem(STORAGE_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data) as WizardDraft;
  } catch {
    return null;
  }
}

/**
 * Contenedor "wizard" de las secciones de tenant: las mismas secciones que usa
 * `/tenants/:id`, pero en secuencia y con el stepper mostrando qué falta.
 */
export function TenantCreateWizard() {
  const navigate = useNavigate();
  const [draft, dispatch] = useReducer<
    (state: WizardDraft, action: WizardAction) => WizardDraft,
    WizardDraft
  >(
    wizardReducer,
    { step: 1, channels: {} } as WizardDraft,
    () => (loadFromLocalStorage() ?? { step: 1, channels: {} }) as WizardDraft,
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  }, [draft]);

  function onCancel() {
    navigate('/tenants');
  }

  /**
   * El paso ACTIVO se marca por dónde está la persona, no por su completitud:
   * uno a medio llenar sigue siendo el actual mientras esté parada ahí.
   */
  const pasos: StepperItem[] = wizardNavItems(draft).map((item) => ({
    id: item.id,
    label: item.label,
    disabled: item.disabled,
    status:
      item.id === String(draft.step) ? 'active' : item.status === 'complete' ? 'done' : 'pending',
  }));

  return (
    <AppShell>
      <div id="tenant-create-wizard" className="mx-auto flex h-full max-w-6xl flex-col">
        <div className="mb-8">
          <h1 className="text-h4 text-content-primary">Crear Nuevo Tenant</h1>
          <p className="text-body-sm text-content-tertiary">
            Cada sección se guarda sola. Podés salir y retomar donde quedaste.
          </p>
        </div>

        <div className="grid flex-1 grid-cols-1 gap-8 md:grid-cols-4">
          <div className="space-y-6 md:col-span-1">
            <Stepper
              items={pasos}
              ariaLabel="Pasos de creación del tenant"
              onSelect={(id) => dispatch({ type: 'goto', step: Number(id) as WizardStep })}
            />
            <OnboardingChecklist tenantId={draft.tenantId} />
          </div>

          <div className="md:col-span-3">
            {draft.step === 1 && <Step1Identity draft={draft} dispatch={dispatch} />}
            {draft.step === 2 && <Step2TelegramChannel draft={draft} dispatch={dispatch} />}
            {draft.step === 3 && <Step3WhatsAppChannel draft={draft} dispatch={dispatch} />}
            {draft.step === 4 && <Step4ErpSelector draft={draft} dispatch={dispatch} />}
            {draft.step === 5 && <Step5BotMode draft={draft} dispatch={dispatch} />}
            {draft.step === 6 && (
              <Step6Summary
                draft={draft}
                dispatch={dispatch}
                onActivate={() => {
                  localStorage.removeItem(STORAGE_KEY);
                  navigate(`/tenants/${draft.tenantId}`);
                }}
              />
            )}
          </div>
        </div>

        {/*
         * El footer NO lleva "Continuar".
         *
         * Cada paso tiene el suyo, y ese botón GUARDA antes de avanzar. Uno
         * global que hiciera `goto step+1` saltearía el guardado y la persona
         * perdería lo que acababa de escribir sin ningún aviso.
         *
         * Sí lleva lo que es global y hoy vive arriba a la derecha, lejos de
         * donde está trabajando quien completa un formulario largo.
         */}
        <StickyFooter
          hint={`Paso ${draft.step} de 6`}
          secondary={
            <Button variant="danger" size="sm" onClick={() => dispatch({ type: 'reset' })}>
              Reiniciar desde cero
            </Button>
          }
          primary={
            <Button variant="outline" size="sm" onClick={onCancel}>
              Guardar borrador y salir
            </Button>
          }
        />
      </div>
    </AppShell>
  );
}
